/**
 * @file src/ui/react/studio/LocalFilesPanel.tsx
 * @description Managing the files on this machine, from inside the studio.
 *
 * The rail at the bottom is the collection you are PLAYING; this is the disk those files sit on.
 * Two different questions, so two surfaces — and this one opens beside the stages rather than on
 * top of them.
 *
 * ## It reserves, it does not overlay
 *
 * The same rule the bottom drawer and the dock already follow: a new piece of chrome takes its own
 * space and the stages reflow into what is left. It publishes its width as `--orbiters-local-panel`
 * on the studio root, which pads itself by exactly that; `ViewportCompositor` re-reads each stage's
 * rect every frame, so the orbiters re-frame themselves and no canvas is ever resized for this.
 * Floating it at a high z-index instead would silently eat whatever sits in that band — the stages'
 * own top-right controls live there.
 *
 * ## Two shapes
 *
 * Desktop: a column down the right, resizable within sane bounds, remembered for next time.
 * Phone: a bottom sheet, because a column on a phone is a column of nothing — the same swap the
 * drawer makes. Either way the body is `LocalFilesBrowser`, the one this app has; the page the
 * main site frames renders that same component.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from 'plantasia.space-design/react';
import { LocalFilesBrowser } from '../../../local/page/LocalFilesBrowser';
import { getT } from '../../../i18n/index.js';

/** The width the panel opens at, and the range a drag may take it to. */
/** Exported so the studio can state the same number when it reports a panel nobody has resized. */
export const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;

/** The phone sheet's height, matched to the drawer's so the two read as one family. */
const SHEET_HEIGHT = 'min(70dvh, 520px)';

const clampWidth = (w: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));

export interface LocalFilesPanelProps {
  open: boolean;
  onClose: () => void;
  /** The width to open at — the one this viewer last left, part of the studio's remembered shape.
   *  Read ONCE, at mount (see the state below). Absent/unusable opens at the default; out of range
   *  is clamped. Desktop only: a phone gets a full-width sheet, which has no width to remember. */
  initialWidth?: number | null;
  /** The width SETTLED — a resize drag ended somewhere new. Only the settled value, and only when it
   *  actually moved. */
  onWidthSettled?: (width: number) => void;
  /**
   * Ref-callback for the panel's outer element — the studio attaches `applyStudioChromeTheme` here,
   * exactly as it does for the dock and the drawer. Without it this surface is not in the chrome
   * scope at all and paints whatever tokens happen to be ambient instead of the user's own preset.
   */
  containerRef?: (node: HTMLElement | null) => void | (() => void);
  /**
   * Put the selected files into the collection this studio is showing. The whole reason management
   * lives in here rather than only on a page: the files are right there, and wanting one in the
   * collection you are playing is the obvious next thought. Absent = not offered.
   */
  onAddToCollection?: ((ids: string[]) => void | Promise<void>) | null;
  /** What that collection is called, so the button can name it rather than say "here". */
  collectionName?: string | null;
  /** Which collection it adds to, so the list can hide the action when everything chosen is in it. */
  collectionKey?: string | null;
  /**
   * Dragging rows out of the list onto the studio. The list owns the gesture; these own everything
   * positional, because only the studio knows what a stage or a rail is.
   */
  onRowsDragMove?: (ids: string[], x: number, y: number) => void;
  onRowsDrop?: (ids: string[], x: number, y: number) => void;
  onRowsDragCancel?: () => void;
  isMobile: boolean;
  /** Where the studio's own top chrome ends — the panel starts below it, never under it. */
  topInset: string;
  /** Where the dock's strip begins — the panel stops above it. Desktop only. */
  bottomInset: string;
  /**
   * Where the sheet sits on a phone. The studio computes it, because only the studio knows whether
   * the dock is a bottom bar or a landscape rail — restating that here would be the second source
   * of truth that gets it wrong the next time the bar moves.
   */
  mobileInset: CSSProperties;
}

export function LocalFilesPanel({
  open,
  onClose,
  initialWidth = null,
  onWidthSettled,
  containerRef,
  onAddToCollection = null,
  collectionName = null,
  collectionKey = null,
  onRowsDragMove,
  onRowsDrop,
  onRowsDragCancel,
  isMobile,
  topInset,
  bottomInset,
  mobileInset,
}: LocalFilesPanelProps) {
  const t = getT();
  // Re-pointed each render so the settle report always reaches the latest callback without the drag
  // handlers being rebuilt (and their listeners re-bound) every time the studio re-renders.
  const onWidthSettledRef = useRef(onWidthSettled);
  onWidthSettledRef.current = onWidthSettled;
  // Opened at the width this machine last left. Read once, and then the panel owns it: a prop that
  // kept overriding would fight the edge under the finger.
  //
  // Once from the first REAL value, though, not once at mount. The width is remembered with the
  // local files, which are read from disk after the studio is already up — so at mount it is still
  // unknown, and a panel that latched that would open at the default for ever.
  const [width, setWidth] = useState(() =>
    Number.isFinite(initialWidth) ? clampWidth(initialWidth as number) : DEFAULT_WIDTH,
  );
  const widthAdopted = useRef(Number.isFinite(initialWidth));
  useEffect(() => {
    if (widthAdopted.current || !Number.isFinite(initialWidth)) return;
    widthAdopted.current = true;
    setWidth(clampWidth(initialWidth as number));
  }, [initialWidth]);
  // The selection lives HERE, not inside the list, because the panel is what offers an action on
  // it that the list itself could not know about.
  const [selected, setSelected] = useState<string[]>([]);
  // A drag is in flight. Escape has to reach it first: cancelling a drag and closing the panel are
  // two different intentions, and one key press should only ever mean the nearer of them.
  // A drag means this panel owns its width outright from now on — a stored value arriving late must
  // never pull it back out from under what the person has just done.
  const draggingRef = useRef(false);
  const dragRef = useRef<{ pointerId: number; startX: number; startWidth: number; settled: number } | null>(null);

  // The reserve the studio root pads itself by. Published rather than passed, so anything else
  // that has to clear the panel reads ONE number and never restates it.
  useEffect(() => {
    const root = document.documentElement;
    const reserve = open && !isMobile ? `${width}px` : '0px';
    root.style.setProperty('--orbiters-local-panel', reserve);
    return () => {
      // A studio that unmounts with the panel open must not leave the whole app padded.
      root.style.setProperty('--orbiters-local-panel', '0px');
    };
  }, [open, isMobile, width]);

  /** Take down whatever resize drag is in flight, without reporting anything. The handlers are read
   *  back out of a ref rather than closed over, so this can be declared before the ones it removes and
   *  can never remove a stale identity. */
  const boundRef = useRef<{
    move?: (e: PointerEvent) => void;
    up?: (e: PointerEvent) => void;
    cancel?: (e: PointerEvent) => void;
  }>({});
  const endDrag = useCallback(() => {
    const bound = boundRef.current;
    if (bound.move) window.removeEventListener('pointermove', bound.move);
    if (bound.up) window.removeEventListener('pointerup', bound.up);
    if (bound.cancel) window.removeEventListener('pointercancel', bound.cancel);
    boundRef.current = {};
    dragRef.current = null;
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const drag = dragRef.current;
    // Only the finger that started this drag resizes it: a second one is a hand resting or a pinch.
    if (!drag || event.pointerId !== drag.pointerId) return;
    // Dragging the LEFT edge: moving left widens, so the delta is inverted.
    const next = clampWidth(drag.startWidth + (drag.startX - event.clientX));
    // Kept on the drag itself, not read back off state on release: a pointerup lands in the same tick
    // as the last pointermove, so the state behind it may not have re-rendered yet.
    drag.settled = next;
    setWidth(next);
  }, []);

  const onDragEnd = useCallback(
    (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return; // another pointer's release is not this one's
      endDrag();
      // Where the edge came to rest, reported once — and only if it actually moved. The width is
      // remembered with the rest of the way this studio was left, so the panel keeps no copy of its own.
      if (drag.settled !== drag.startWidth) onWidthSettledRef.current?.(drag.settled);
    },
    [endDrag],
  );

  // Cancelled (the browser claimed the pointer): the edge was never let go of, so nothing is reported.
  const onDragCancel = useCallback(
    (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      endDrag();
    },
    [endDrag],
  );

  const onDragStart = useCallback(
    (event: React.PointerEvent) => {
      // One drag at a time — see the drawer's handle for why a second press must not stack listeners.
      endDrag();
      // The person has hold of the edge: from THIS moment the width is theirs. Marked here rather
      // than on the first move, because the drag measures from the width it starts at — a stored
      // width landing between the press and the first move would otherwise shift the panel under the
      // finger, and the drag would carry on from a width that is no longer on screen.
      widthAdopted.current = true;
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: width,
        settled: width,
      };
      boundRef.current = { move: onDragMove, up: onDragEnd, cancel: onDragCancel };
      window.addEventListener('pointermove', onDragMove);
      window.addEventListener('pointerup', onDragEnd);
      window.addEventListener('pointercancel', onDragCancel);
    },
    [endDrag, onDragCancel, onDragEnd, onDragMove, width],
  );

  useEffect(() => endDrag, [endDrag]);

  // Escape closes it, as it does every other studio surface. Only while it is open, so it never
  // competes with the stage's own Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      // The list cancels its own drag on Escape. Closing on the same press would take the panel
      // away as well, and the person was only calling off the drag.
      if (event.key === 'Escape' && !draggingRef.current) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, open]);

  // Reopening to a selection made before it was closed would put an old choice behind a
  // destructive button, so closing forgets it.
  useEffect(() => {
    if (!open) setSelected([]);
  }, [open]);

  if (!open) return null;

  const title = t('localFiles.title', 'Local files');

  return (
    <aside
      ref={containerRef}
      aria-label={title}
      style={{
        position: 'fixed',
        // Desktop: just above the studio root (41), which has already reserved this strip, so there
        // is nothing underneath to fight. Mobile: above the card drawer (45), because there the
        // sheet is a surface you open, use and close rather than chrome that lives on screen.
        zIndex: isMobile ? 46 : 42,
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        // The drawer's own frosted surface, in the USER chrome tokens the studio writes onto this
        // element (`containerRef` → `applyStudioChromeTheme`). `--color-*`, not the raw palette:
        // those are what the preset actually sets, which is why the dock and the drawer use them.
        // color-mix rather than a flat fill so the blur still reads through.
        background: 'color-mix(in oklab, var(--color-background) 62%, transparent)',
        backdropFilter: 'blur(12px)',
        color: 'var(--color-foreground)',
        ...(isMobile
          ? {
              ...mobileInset,
              height: SHEET_HEIGHT,
            }
          : {
              top: topInset,
              bottom: bottomInset,
              right: 0,
              width,
            }),
      }}
    >
      {/* Desktop only: the edge that resizes it. A phone sheet is the height it is. */}
      {!isMobile ? (
        <div
          role="separator"
          data-orbiter-local-panel-resize=""
          aria-orientation="vertical"
          aria-label={t('localFiles.resize', 'Resize the panel')}
          onPointerDown={onDragStart}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'col-resize',
            touchAction: 'none',
          }}
        />
      ) : null}

      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 12px 6px',
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('localFiles.close', 'Close')}>
          <X aria-hidden="true" />
        </Button>
      </header>

      {/* The panel carries the heading, so the browser does not draw its own — two headings for one
          list read as two lists, the same reason the main site's frame draws none. */}
      <LocalFilesBrowser
        showTitle={false}
        className="ps-local-page--panel"
        // The panel IS the frame; a bordered box inside a bordered box is two boxes.
        frame={false}
        // A side panel is always narrow. Declared rather than measured — the two-line rows are
        // what this width is for, and nothing has to observe anything to know that.
        compactLayout
        selected={selected}
        onSelectedChange={setSelected}
        drag={
          onRowsDrop
            ? {
                onDragStart: () => {
                  draggingRef.current = true;
                },
                onDragMove: (ids, event) => onRowsDragMove?.(ids, event.clientX, event.clientY),
                onDragEnd: (ids, event) => {
                  draggingRef.current = false;
                  onRowsDrop(ids, event.clientX, event.clientY);
                },
                onDragCancel: () => {
                  draggingRef.current = false;
                  onRowsDragCancel?.();
                },
              }
            : undefined
        }
        // Adding sits in the SAME bar as removing, at the bottom of the list. Split across the
        // surface — one at the top, one at the bottom — they read as unrelated, and half of them
        // has to be hunted for.
        hostAction={
          onAddToCollection
            ? {
                label: collectionName
                  ? (t('localFiles.addToNamed', { name: collectionName, defaultValue: 'Add to {{name}}' }) as string)
                  : (t('localFiles.addToCollection', 'Add to this collection') as string),
                icon: <Plus aria-hidden="true" />,
                targetScope: collectionKey,
                onAction: (ids) => onAddToCollection(ids),
              }
            : null
        }
      />
    </aside>
  );
}
